import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../state/store";
import { DocumentTemplate } from "../../../models/ConsentDocument";
import { Form } from "../../form/Form";
import { ReactiveFormInput } from "../../form/ReactiveFormInput";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { Fragment, useEffect, useState } from "react";
import { Modal } from "../../Modal";
import { toast, ToastContainer } from "react-toastify";
import { LoadingMask } from "../../LoadingMask";
import { ReactiveFormInputArea } from "../../form/ReactiveFormInputArea";
import { ReactiveFormSelect } from "../../form/ReactiveFormSelect";
import { getLanguageTypes } from "../../../enums/LanguageType";
import { ReactiveFormUploadFile } from "../../form/ReactiveFormUploadFile";
import Button from "../../Button";
import { ArrowRightCircleIcon } from "@heroicons/react/24/outline";
import mammoth from "mammoth";
import { firebaseApp, getStorage } from "../../../firebase/firebaseConfig";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { Toast } from "../../Toast";
import { User } from "../../../models/User";
import { createDocumentTemplate, updateDocumentTemplate } from "../../../state/templates/actions";

interface Props {
  info: {
    template?: DocumentTemplate,
    currentUser?: User,
    update: boolean,
    show: boolean
  };
  onClose: () => void;
}

export const AgencyDocusignTemplateModal: React.FC<Props> = ({
  info,
  onClose,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const [continueToAddTemplate, setContinueToAddTemplate] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [templatesError, setTemplatesError] = useState<any>(null);
    const [showTemplateError, setShowTemplateError] = useState(false);

    useEffect(() => {
        if (showTemplateError) {
            Toast(t(templatesError?.reason ?? "SOMETHING_WENT_WRONG"), t(templatesError?.cause_info));
            setShowTemplateError(false);
        }
    }, [showTemplateError, templatesError, t]);

    const methods = useForm<DocumentTemplate>({
        defaultValues: {},
    });
    const { handleSubmit, control, setValue, getValues, reset } = methods;

    useEffect(() => {
        info.template ? reset(info.template) : reset({})

        if(info.update) {
            setContinueToAddTemplate(true);
        } else {
            setContinueToAddTemplate(false);
        }
    }, [info.template, reset]);

    const onSubmitInvalid: SubmitErrorHandler<DocumentTemplate> = (data) => {
        toast(t("PLEASE_ENTER_ALL_POLICY_REQUIRED_FIELDS"));
    }

    const onSubmit: SubmitHandler<DocumentTemplate> = async (data: DocumentTemplate) => {
        if(info.update) {
            setIsLoading(true)
            
            dispatch(updateDocumentTemplate({templateId: info.template!.id!, data: data})).then((e) => {
                if (e.type === "docusign/updateDocumentTemplate/rejected") {
                    setTemplatesError(e.payload);
                    setShowTemplateError(true);
                    setIsLoading(false);
                } else {
                    setIsLoading(false);
                    toast(t("SUCCESSFULLY_UPDATE_DOCUSIGN_TEMPLATE"));
                    closeModal();
                }
            });
        } else {
            try {
                setIsLoading(true)
                const storage = getStorage(firebaseApp);
                const fileRef = ref(storage, `templates/${selectedTemplate.name}`);
                let snapshot = await uploadBytes(fileRef, selectedTemplate);
                let url = (await getDownloadURL(snapshot.ref)).toString();

                data.document_url = url;

                dispatch(createDocumentTemplate({data: data})).then((e) => {
                    if (e.type === "docusign/createDocumentTemplate/rejected") {
                        setTemplatesError(e.payload);
                        setShowTemplateError(true);
                        setIsLoading(false);
                    } else {
                        setIsLoading(false);
                        toast(t("SUCCESSFULLY_CREATE_DOCUSIGN_TEMPLATE"));
                        closeModal();
                    }
                });
            } catch(error) {
                setTemplatesError(error);
                setShowTemplateError(true);
            }
        }
    };

    const handleDocusignTemplate = (e: any) => {
        const file = e[0];
        if(!file) {
            toast(t("PLEASE_SELECT_A_VALID_FILE"));
            return;
        }

        const reader = new FileReader()
        reader.onload = async(e: any) => {
            const arrayBuffer = e.target.result;

            try {
                const { value : text} = await mammoth.extractRawText({ arrayBuffer })
                const hasRequiredText = text.includes("Firma del Client") || text.includes("Client's Signature")
                if(!hasRequiredText) {
                    toast(t('TEMPLATE_REQUIRED_TEXT_ERROR'));
                    return;
                } else {
                    setSelectedTemplate(file);
                    setValue('document_name', file.name);
                }
            } catch(error) {
                console.error(error);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleDocusignTemplateDrop = (e: any) => {
        const file = e[0];
        if(!file) {
            toast(t("PLEASE_SELECT_A_VALID_FILE"));
            return;
        }

        const reader = new FileReader()
        reader.onload = async(e: any) => {
            const arrayBuffer = e.target.result;

            try {
                const { value : text} = await mammoth.extractRawText({ arrayBuffer })
                const hasRequiredText = text.includes("Firma del Client") || text.includes("Client's Signature")
                if(!hasRequiredText) {
                    toast(t('TEMPLATE_REQUIRED_TEXT_ERROR'));
                    return;
                } else {
                    setSelectedTemplate(file);
                    setValue('document_name', file.name);
                }
            } catch(error) {
                console.error(error);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const closeModal = () => {
        reset({})
        setContinueToAddTemplate(false);
        setSelectedTemplate(null);
        setTemplatesError(null);
        onClose();
    };
    
    return(
        <>
            <Modal
                isOpen={info.show}
                onClose={closeModal}
                label={info.update ? t("UPDATE_TEMPLATE") : t("ADD_TEMPLATE")}
                onSave={handleSubmit(onSubmit, onSubmitInvalid)}
                hideSaveButton={!continueToAddTemplate}
            >
                <div className="w-full flex flex-wrap">
                    {   !continueToAddTemplate && 
                        <div className="w-full px-3 pb-5">
                            <div className="text-xl font-bold text-black pb-2">{t("IMPORTANT_NOTES")}</div>
                            <div className="p-4 bg-blue-50 border border-dashed border-2 border-blue-300 rounded-md">
                                <div className="pb-3 font-bold">{t('READ_BEFORE_ADD_TEMPLATE')}</div>
                                <ul className="list-disc list-inside space-y-3">
                                    <li>
                                        {`${t("MUST_CONTAIN_MESSAGE_1")}`}{" "} <strong>{`${t("MUST_CONTAIN_MESSAGE_2")}`}</strong>{" "} {`${t("MUST_CONTAIN_MESSAGE_3")}`}
                                        <ul className="list-disc list-inside ml-6 mt-2">
                                            <li><strong>"Firma del Cliente:"</strong> {`(${t("FOR_SPANISH_TEMPLATES")})`}</li>
                                            <li><strong>"Client's Signature:"</strong> {`(${t("FOR_ENGLISH_TEMPLATES")})`}</li>
                                        </ul>
                                    </li>
                                    <li>
                                        {`${t("SUPPORTS_PLACHOLDER_MESSAGE_1")}`}{" "} <strong>{`${t("SUPPORTS_PLACHOLDER_MESSAGE_2")}`}</strong>:
                                        <ul className="list-disc list-inside ml-6 mt-1">
                                            <li><strong>{`{{date}}`}</strong> - {`${t("DATE_PLACEHOLDER")}`}</li>
                                            <li><strong>{`{{agent_name}}`}</strong> - {`${t("AGENT_NAME_PLACEHOLDER")}`}</li>
                                            <li><strong>{`{{agent_npn}}`}</strong> - {`${t("AGENT_NPN_PLACEHOLDER")}`}</li>
                                            <li><strong>{`{{agent_email}}`}</strong> - {`${t("AGENT_EMAIL_PLACEHOLDER")}`}</li>
                                            <li><strong>{`{{agent_phone}}`}</strong> - {`${t("AGENT_PHONE_PLACEHOLDER")}`}</li>
                                            <li><strong>{`{{client_name}}`}</strong> - {`${t("CLIENT_NAME_PLACEHOLDER")}`}</li>
                                            <li><strong>{`{{client_birthday}}`}</strong> - {`${t("CLIENT_BIRHTDAY_PLACEHOLDER")}`}</li>
                                            <li><strong>{`{{requested_date}}`}</strong> - {`${t("REQUESTED_DATE_PLACEHOLDER")}`}</li>
                                        </ul>
                                    </li>
                                    <li>
                                        {`${t("PLACEHOLDER_EXACT_FORMAT")}`}{" "} <strong>{"{{"}</strong> {`${t("AND")}`}{" "} <strong>{"}}"}</strong>
                                    </li>
                                    <li>
                                        {`${t("DOCUMENT_EXTENSION")}`}{" "} <strong>{"Microsoft Word (.docx)"}</strong> {`${t("DOCUMENT_EXTENSION_2")}`}
                                    </li>
                                    <li className="text-red-700 font-semibold">
                                        {`${t("SIGNATURE_TEXT_MISSING")}`}
                                    </li>
                                </ul>
                            </div>
                            <div className="w-full flex items-center justify-end pt-5">
                                <Button onClick={() => setContinueToAddTemplate(true)} classNames="ml-2">
                                    <span className="flex items-center font-semibold pr-3">
                                    <div className="w-8 p-1 aspect-square mr-2"><ArrowRightCircleIcon /></div>
                                        {t('CONTINUE_ADD_TEMPLATE')}
                                    </span>
                                </Button>
                            </div>
                        </div>
                    }
                    {   continueToAddTemplate &&
                        <Fragment>
                            <ReactiveFormInput
                                control={control}
                                className="md:w-1/3"
                                label={t("LABEL")}
                                name={`name`}
                                isRequired
                            />
                            <ReactiveFormInput
                                control={control}
                                className="md:w-1/3"
                                label={t("DOCUMENT_NAME")}
                                name={`document_name`}
                                isDisabled
                                isRequired
                            />
                            <ReactiveFormSelect
                                className="md:w-1/3"
                                control={control}
                                label={t("LANGUAGE")}
                                name={`language`}
                                options={getLanguageTypes()}
                                isRequired
                                isDisabled={info.update}
                            />
                            {   (!selectedTemplate && !info.template) &&
                                <ReactiveFormUploadFile
                                    control={control}
                                    className="md:w-1/3"
                                    label={t("SELECT_TEMPLATE")}
                                    name="template"
                                    handleOnChange={handleDocusignTemplate}
                                    handleOnDrop={handleDocusignTemplateDrop}
                                    accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                />
                            }
                            {   selectedTemplate  &&
                                <div className="w-full md:w-1/3 px-3">
                                    <div className="border border-dashed border-2 border-gray-200 text-gray-700 rounded py-3 px-4">
                                        <div className="font-bold">{t('FILE_SELECTED')}</div>
                                        <div className="grid justify-items-center text-center pt-3 pb-2">
                                            <p>{selectedTemplate.name}</p>
                                            <Button isTerciary onClick={() => {setSelectedTemplate(null)}} classNames='text-red-500 mt-3'>
                                                <span className='flex items-center font-semibold px-3 py-1'>
                                                    {t('REMOVE_FILE')}
                                                </span>
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            }
                            {   info.template &&
                                <div className="w-full md:w-1/3 px-3">
                                    <div className="border border-dashed border-2 border-gray-200 text-gray-700 rounded py-3 px-4">
                                        <div className="font-bold">{t('FILE_SELECTED')}</div>
                                        <div className="grid justify-items-center text-center pt-3 pb-2">
                                            <p>{info.template.document_name}</p>
                                        </div>
                                    </div>
                                </div>
                            }
                            <ReactiveFormInputArea
                                control={control}
                                className="md:w-2/3"
                                label={t("DESCRIPTION")}
                                name={`description`}
                            />
                        </Fragment>
                    }
                </div>
            </Modal>
            <ToastContainer progressStyle={{ background: "#D4AF37" }} />
            {isLoading && <LoadingMask/>}
        </>
    )
}